import api, { apiFormData } from "../utils/api";

// Types
export interface ChatParticipant {
  uid: string;
  display_name: string;
  avatar?: string | null;
  email?: string | null;
  company_name?: string | null;
  // ID de la compañía a la que pertenece el usuario. Útil sobre todo en chats
  // de grupo / viaje donde los miembros pueden venir de tenants distintos
  // (p. ej. un admin reseller que charla con clientes de otra compañía).
  company_id?: string | null;
}

export interface ChatMessage {
  id: string;
  chat_id: string;
  sender_id: string;
  sender_name: string;
  content: string;
  type: "text" | "file" | "system";
  timestamp: number;
  read_by: string[];
  attachment_url?: string | null;
  attachment_name?: string | null;
  attachment_type?: string | null;
  attachment_size?: number | null;
}

export interface Chat {
  id: string;
  type: "one-to-one" | "group" | "trip";
  name?: string | null;
  creator_id: string;
  participants: ChatParticipant[];
  participant_ids: string[];
  company_id: string;
  trip_id?: string | null;
  trip_name?: string | null;
  trip_description?: string | null;
  route_id?: string | null;
  route_name?: string | null;
  last_message?: string | null;
  last_message_at?: number | null;
  last_message_sender_id?: string | null;
  unread_count: number;
  created_at: number;
  updated_at: number;
}

export interface CreateChatPayload {
  type: "one-to-one" | "group" | "trip";
  participants: ChatParticipant[];
  name?: string;
  trip_id?: string;
  trip_name?: string;
  route_id?: string;
}

export interface SendMessagePayload {
  content: string;
  type?: "text" | "file" | "system";
  attachment_url?: string;
  attachment_name?: string;
  attachment_type?: string;
  attachment_size?: number;
}

export type UploadCategory = "images" | "videos" | "audio" | "files";

export interface UploadFileResponse {
  file_path: string;
  file_name: string;
  file_type: string;
  file_size: number;
  category: UploadCategory;
}

// API Functions (misma baseURL, auth e interceptores que el resto de la app)
export const chatApiService = {
  async getChats(params?: { limit?: number; before?: number }): Promise<Chat[]> {
    const response = await api.get("/chats", { params });
    return response.data.chats;
  },

  async getChat(chatId: string): Promise<Chat> {
    const response = await api.get(`/chats/${chatId}`);
    return response.data.chat;
  },

  /**
   * Crea (o recupera) un chat. Para 1:1 el backend deduplica por
   * `participant_ids` ordenados: si ya existía devuelve `200` con el chat
   * existente y un campo `message` informativo; si lo creó devuelve `201`.
   *
   * Exponemos `isNew` para que el caller pueda decidir si emitir el evento
   * `chat-created` por socket (solo tiene sentido cuando es realmente
   * nuevo, para no spammear a los demás participantes).
   */
  async createChat(
    data: CreateChatPayload,
  ): Promise<{ chat: Chat; isNew: boolean }> {
    const response = await api.post("/chats", data);
    const isNew = response.status === 201 || !response.data?.message;
    return { chat: response.data.chat, isNew };
  },

  async getChatMessages(
    chatId: string,
    params?: { limit?: number; before?: number }
  ): Promise<ChatMessage[]> {
    const response = await api.get(`/chats/${chatId}/messages`, { params });
    return response.data.messages;
  },

  async sendMessage(
    chatId: string,
    data: SendMessagePayload
  ): Promise<ChatMessage> {
    const response = await api.post(`/chats/${chatId}/messages`, data);
    return response.data.message;
  },

  async markAsRead(chatId: string): Promise<void> {
    await api.patch(`/chats/${chatId}/read`);
  },

  async searchUsers(query: string, limit = 20): Promise<ChatParticipant[]> {
    const response = await api.get("/chats/search-users", {
      params: { q: query, limit },
    });
    return response.data.users;
  },

  async uploadFile(
    file: File,
    onProgress?: (percent: number) => void,
  ): Promise<UploadFileResponse> {
    const formData = new FormData();
    formData.append("file", file, file.name);
    const response = await apiFormData.post("/chats/upload-file", formData, {
      // Leave Content-Type unset so the browser inserts the correct
      // multipart/form-data boundary. axios respects an absent header here.
      transformRequest: [(data) => data],
      onUploadProgress: onProgress
        ? (event) => {
            if (!event.total) return;
            const pct = Math.round((event.loaded * 100) / event.total);
            onProgress(pct);
          }
        : undefined,
    });
    return response.data;
  },

  async deleteChat(chatId: string): Promise<void> {
    await api.delete(`/chats/${chatId}`);
  },
};

export default chatApiService;
